import { Component, HostListener, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { ToastService } from '@app/core/services/toast.service';
import { LoginService } from '@app/shared/services/login.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DiscrepanciesService } from 'api/services';
import { AuthService } from '@app/core/services/auth.service';
import { lastValueFrom } from 'rxjs';
import { TermsOfServiceNotAcceptedError } from '@app/shared/constants/http-context-tokens';
import { MatDialog } from '@angular/material/dialog';
import { RegisterOrganizationComponent } from '@app/core/components/dialogs/register-organization/register-organization.component';
import {
    DisplayGlobalAnnouncement,
    GlobalAnnouncementsService,
} from '@app/shared/services/global-announcements.service';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { UnauthFooterComponent } from '../../components/unauth-footer/unauth-footer.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    imports: [
        MatIcon,
        MatButton,
        MatTooltip,
        NgClass,
        UnauthFooterComponent,
        TranslateModule,
    ],
})
export class LoginComponent implements OnInit {
    @HostListener('document:keydown.enter', ['$event'])
    loginForm = new FormGroup({
        username: new FormControl('', Validators.required),
        password: new FormControl('', Validators.required),
    });
    isLoading = signal(false);
    hideInfo = signal(false);
    folderId: number;

    announcements = signal<DisplayGlobalAnnouncement[]>([]);

    constructor(
        private toastr: ToastService,
        private translate: TranslateService,
        private storageService: StorageService,
        private activeRoute: ActivatedRoute,
        private router: Router,
        private authService: AuthService,
        private sessionService: SessionService,
        private discrepanciesService: DiscrepanciesService,
        private loginService: LoginService,
        private matDialog: MatDialog,
        private globalAnnouncementsService: GlobalAnnouncementsService
    ) {}

    async ngOnInit(): Promise<void> {
        this.activeRoute.queryParams
            .pipe(untilDestroyed(this))
            .subscribe(params => {
                this.folderId = params.folderId;
                // Store return URL for redirect after login
                if (params.returnUrl) {
                    localStorage.setItem('returnUrl', params.returnUrl);
                }
            });

        this.hideInfo.set(
            this.storageService.getItem('hideInfo') == 'true' ? true : false
        );

        this.authService.configured$
            .pipe(untilDestroyed(this))
            .subscribe(async configured => {
                if (configured) {
                    const isLoggedIn = await this.authService.isLoggedIn();
                    if (isLoggedIn) {
                        this.isLoading.set(true);
                        const { success, notAllowed, termsPending } =
                            await this.attemptUserLoad();
                        if (termsPending) {
                            // ApiInterceptor already navigated to /settings/tos
                            this.isLoading.set(false);
                            return;
                        }
                        if (!success) {
                            this.isLoading.set(false);
                            // For a not-allowed organization stay on /login so
                            // the warning remains visible; otherwise redirect.
                            await this.sessionService.logout(notAllowed);
                            return;
                        }

                        // check if organization has registered in addons
                        if (!this.sessionService.hasRegisteredInAddons()) {
                            if (this.sessionService.isOrganizationAdmin) {
                                this.matDialog
                                    .open(RegisterOrganizationComponent, {
                                        width: '55vw',
                                        disableClose: true,
                                    })
                                    .afterClosed()
                                    .subscribe(async dialogResult => {
                                        if (dialogResult) {
                                            this.toastr.success(
                                                this.translate.instant(
                                                    'ADDONS.REGISTER_ORGANIZATION_DIALOG.ON_SUCCESS'
                                                )
                                            );

                                            await this.finishAuthenticationProcess();
                                        } else {
                                            this.isLoading.set(false);
                                            await this.sessionService.logout(
                                                true
                                            );
                                        }
                                    });
                            } else {
                                this.toastr.warning(
                                    this.translate.instant(
                                        'ADDONS.ORGANIZATION_NOT_REGISTERED'
                                    )
                                );
                                this.isLoading.set(false);
                                await this.sessionService.logout(true);
                                return;
                            }
                        } else {
                            await this.finishAuthenticationProcess();
                        }
                    }
                }
            });

        await this.loadAndFilterAnnouncements();
    }

    /**
     * Handles Keycloak login flow
     */
    async loginWithKeycloak(): Promise<void> {
        try {
            this.isLoading.set(true);
            this.authService.initLogin();
        } catch (error) {
            this.isLoading.set(false);
            console.error('Keycloak login failed:', error);
            this.toastr.error(this.translate.instant('ERROR.LOGIN'));
        }
    }

    /**
     * Loads the discrepancy choices for the app and save them into the localStorage.
     */
    private async loadDiscrepancyChoices(): Promise<void> {
        try {
            const response = await lastValueFrom(
                this.discrepanciesService.getDiscrepanciesChoices()
            );
            this.storageService.setItem('discrepancyChoices', response);
        } catch (error) {
            console.error('Failed to load discrepancy choices:', error);
        }
    }

    private async attemptUserLoad(): Promise<{
        success: boolean;
        notAllowed: boolean;
        termsPending?: boolean;
    }> {
        for (let attempt = 0; attempt < 5; attempt++) {
            try {
                await this.sessionService.loadUserFromApi();
                return { success: true, notAllowed: false };
            } catch (error) {
                // Deterministic as well: retrying cannot help and logging out
                // would undo the interceptor's redirect to /settings/tos.
                if (error instanceof TermsOfServiceNotAcceptedError) {
                    return {
                        success: false,
                        notAllowed: false,
                        termsPending: true,
                    };
                }

                // The organization is not unlocked for c4l-addons. This is
                // deterministic, so stop retrying and warn immediately.
                if (this.sessionService.isOrganizationNotAllowedError(error)) {
                    this.toastr.warning(
                        this.translate.instant(
                            'ADDONS.ORGANIZATION_NOT_REGISTERED'
                        )
                    );
                    return { success: false, notAllowed: true };
                }

                if (attempt === 4) {
                    this.toastr.error(
                        this.translate.instant('LOGIN.ERROR-LOADING-USER')
                    );
                    return { success: false, notAllowed: false };
                }

                // Wait before retrying
                await new Promise(res => setTimeout(res, 600));
            }
        }

        return { success: false, notAllowed: false };
    }

    /**
     * Finalizes the authentication process by loading discrepancy choices and routing the user.
     * @private
     */
    private async loadAndFilterAnnouncements(): Promise<void> {
        try {
            const result =
                await this.globalAnnouncementsService.getGlobalAnnouncements();

            const currentTimestamp = Date.now();

            this.announcements.set(
                result.announcements
                    .filter(announcement => {
                        const hasStarted =
                            announcement.startingAt <= currentTimestamp;
                        const hasNotEnded =
                            announcement.endingAt === null ||
                            announcement.endingAt === undefined ||
                            announcement.endingAt > currentTimestamp;

                        return hasStarted && hasNotEnded;
                    })
                    .sort((a, b) => b.startingAt - a.startingAt)
            );

            if (result.hasFailures) {
                this.showAnnouncementLoadError();
            }
        } catch (error) {
            this.showAnnouncementLoadError();
        }
    }

    private showAnnouncementLoadError(): void {
        this.toastr.error(
            this.translate.instant('ADMIN-PANEL.ANNOUNCEMENTS.GET-ERROR')
        );
    }

    private async finishAuthenticationProcess() {
        await this.loadDiscrepancyChoices();

        this.isLoading.set(false);
        // Use the existing LoginService logic to determine the correct dashboard
        this.loginService.routeToPage(
            this.sessionService.getSession()?.termsOfService?.accepted
        );
    }
}
